// API client for the webhooks + system-config admin surface (P3).

import { apiFetch } from './api';

export type WebhookEvent =
  | 'visitor.identified'
  | 'session.started'
  | 'session.assigned'
  | 'session.ended'
  | 'message.created'
  | 'ticket.created'
  | 'ticket.updated'
  | 'email.bounced';
/** 'template': the hook's own body. 'standard': the signed-envelope JSON
 *  { id, type, created_at, site, data } (docs/webhooks.md). */
export type PayloadFormat = 'template' | 'standard';
export type WebhookMethod = 'POST' | 'PUT';
export type WebhookAuthType = 'none' | 'basic' | 'bearer' | 'header';
// Delivery channel: 'http' fires a request to a URL; 'whatsapp' sends the body
// as a WhatsApp message to every site agent with a phone number.
export type WebhookKind = 'http' | 'whatsapp';

export interface WebhookHeader {
  name: string;
  value: string;
}

export interface Webhook {
  id: string;
  siteId: string;
  event: WebhookEvent;
  kind: WebhookKind;
  name: string;
  url: string;
  method: WebhookMethod;
  authType: WebhookAuthType;
  authValue: string | null;
  headerName: string | null;
  headers: WebhookHeader[];
  bodyTemplate: string;
  payloadFormat: PayloadFormat;
  /** Has a signing secret: every request carries X-Openline-Signature. */
  signing: boolean;
  enabled: boolean;
  requireVisitorInfo: boolean;
  lastTestedAt: string | null;
  lastTestStatus: number | null;
  createdAt: string;
}

// The editable subset sent on create/update/test/preview.
export interface WebhookDraft {
  event: WebhookEvent;
  kind: WebhookKind;
  name: string;
  url: string;
  method: WebhookMethod;
  authType: WebhookAuthType;
  authValue: string | null;
  headerName: string | null;
  headers: WebhookHeader[];
  bodyTemplate: string;
  payloadFormat: PayloadFormat;
  enabled: boolean;
  requireVisitorInfo: boolean;
  lastTestStatus?: number | null;
  /** Set when testing/previewing a saved hook, so it is signed with its secret. */
  id?: string;
}

export interface WebhookDelivery {
  id: string;
  siteId: string | null;
  event: string;
  hookId: string | null;
  kind: 'http' | 'email' | 'whatsapp';
  target: string | null;
  requestBody: string | null;
  responseStatus: number | null;
  responseSnippet: string | null;
  status: 'success' | 'failed' | 'skipped';
  error: string | null;
  createdAt: string;
}

export interface TestResult {
  status: 'success' | 'failed';
  responseStatus: number | null;
  responseSnippet: string;
  requestBody: string;
  requestHeaders: Record<string, string>;
  durationMs: number;
  ok2xx: boolean;
}

/** One attempt in the delivery history. */
export interface DeliveryAttempt {
  id: string;
  attempt: number | null;
  status: 'success' | 'failed' | 'skipped';
  responseStatus: number | null;
  responseSnippet: string | null;
  requestBody: string | null;
  requestHeaders: Record<string, string> | null;
  durationMs: number | null;
  nextRetryAt: string | null;
  manual: boolean;
  target: string | null;
  createdAt: string;
}

export type DeliveryStatus = 'delivered' | 'retrying' | 'failed' | 'skipped';

/** One event sent to one hook: its attempts, retries and resends. */
export interface DeliveryGroup {
  key: string;
  eventId: string | null;
  hookId: string | null;
  hookName: string | null;
  event: string;
  kind: 'http' | 'email' | 'whatsapp';
  target: string | null;
  status: DeliveryStatus;
  firstAt: string;
  lastAt: string;
  nextRetryAt: string | null;
  canResend: boolean;
  attempts: DeliveryAttempt[];
}

export interface DeliveryFilters {
  hookId?: string;
  event?: string;
  status?: DeliveryStatus;
}

export interface PreviewResult {
  url: string;
  headers: Record<string, string>;
  body: string;
}

/**
 * Providers the AI orchestrator can drive. anthropic/openai/gemini are
 * key-only: canonical host, model list fetched live via `listAiModels`.
 * 'openline'/'custom' still need a typed model, and 'custom' a base URL too.
 */
export type AiProvider = 'anthropic' | 'openai' | 'gemini' | 'openline' | 'custom';

/** Providers with live model discovery — drives the dropdown-vs-textbox split. */
export const KEY_ONLY_AI_PROVIDERS = ['anthropic', 'openai', 'gemini'] as const;

export const isKeyOnlyAiProvider = (p: AiProvider): boolean =>
  (KEY_ONLY_AI_PROVIDERS as readonly string[]).includes(p);

export const AI_PROVIDER_LABEL: Record<AiProvider, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  gemini: 'Google Gemini',
  openline: 'Prakash Software (Ollama)',
  custom: 'Custom (OpenAI-compatible)',
};

export interface SystemConfig {
  fromName: string | null;
  fromEmail: string | null;
  emailEnabled: boolean;
  /** IANA zone the readable timestamps in outgoing email use. Null = UTC. */
  timezone: string | null;
  subjectTemplate: string;
  htmlTemplate: string;
  textTemplate: string;
  updatedAt: string;
  /** The server's SMTP sender (SMTP_* env), read-only. Never the login. */
  smtp:
    | { configured: true; host: string; port: number; secure: boolean; fromEmail: string; fromName: string | null }
    | { configured: false; reason: string };
  // System-wide AI assistant defaults. aiApiKey is never returned — only these.
  aiProvider: AiProvider;
  aiModel: string | null;
  aiBaseUrl: string | null;
  aiApiKeySet: boolean;
  aiKeyLast4: string | null;
  // Sampling parameters in real units (the API unscales them). null anywhere
  // means "not set" — the provider's own default applies.
  aiTemperature: number | null;
  aiTopP: number | null;
  aiMaxTokens: number | null;
  aiStopSequences: string[];
  // Speech-to-text (transcription) defaults. Key never returned.
  transcriptionProvider: 'groq';
  transcriptionModel: string | null;
  transcriptionKeySet: boolean;
  transcriptionKeyLast4: string | null;
  /** Blends agent time into cost per resolution. null = AI cost only. */
  agentHourlyCostUsd: number | null;
}

const base = (siteId: string) => `/api/admin/sites/${siteId}/webhooks`;

export const listWebhooks = (siteId: string) =>
  apiFetch<{ webhooks: Webhook[]; signingAvailable: boolean }>(base(siteId));

export const createWebhook = (siteId: string, draft: WebhookDraft) =>
  apiFetch<{ webhook: Webhook }>(base(siteId), {
    method: 'POST',
    body: JSON.stringify(draft),
  }).then((r) => r.webhook);

export const updateWebhook = (siteId: string, id: string, draft: Partial<WebhookDraft>) =>
  apiFetch<{ webhook: Webhook }>(`${base(siteId)}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(draft),
  }).then((r) => r.webhook);

export const deleteWebhook = (siteId: string, id: string) =>
  apiFetch<{ ok: boolean }>(`${base(siteId)}/${id}`, { method: 'DELETE' });

export const testWebhook = (siteId: string, draft: WebhookDraft) =>
  apiFetch<TestResult>(`${base(siteId)}/test`, {
    method: 'POST',
    body: JSON.stringify(draft),
  });

export const previewWebhook = (siteId: string, draft: WebhookDraft) =>
  apiFetch<PreviewResult>(`${base(siteId)}/preview`, {
    method: 'POST',
    body: JSON.stringify(draft),
  });

export function listDeliveries(siteId: string, filters: DeliveryFilters = {}) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) if (v) q.set(k, v);
  const qs = q.toString();
  return apiFetch<{ deliveries: DeliveryGroup[] }>(`${base(siteId)}/deliveries${qs ? `?${qs}` : ''}`).then(
    (r) => r.deliveries,
  );
}

export const resendDelivery = (siteId: string, hookId: string, eventId: string) =>
  apiFetch<{ queued: boolean }>(`${base(siteId)}/deliveries/resend`, {
    method: 'POST',
    body: JSON.stringify({ hookId, eventId }),
  });

export const revealSigningSecret = (siteId: string, hookId: string) =>
  apiFetch<{ secret: string }>(`${base(siteId)}/${hookId}/secret`).then((r) => r.secret);

/** Create or rotate: the old secret stops working at once. */
export const rotateSigningSecret = (siteId: string, hookId: string) =>
  apiFetch<{ secret: string }>(`${base(siteId)}/${hookId}/secret`, { method: 'POST' }).then((r) => r.secret);

// ─── system-config (super-admin) ───
export const getSystemConfig = () =>
  apiFetch<{ systemConfig: SystemConfig }>('/api/admin/system-config').then((r) => r.systemConfig);

export interface SystemConfigPatch {
  fromName?: string | null;
  fromEmail?: string | null;
  emailEnabled?: boolean;
  timezone?: string | null;
  subjectTemplate?: string;
  htmlTemplate?: string;
  textTemplate?: string;
  aiProvider?: AiProvider;
  aiModel?: string | null;
  aiBaseUrl?: string | null;
  // Write-only: non-empty sets the key, null clears it, omit leaves unchanged.
  aiApiKey?: string | null;
  aiTemperature?: number | null;
  aiTopP?: number | null;
  aiMaxTokens?: number | null;
  aiStopSequences?: string[] | null;
  transcriptionProvider?: 'groq';
  transcriptionModel?: string | null;
  transcriptionApiKey?: string | null;
  agentHourlyCostUsd?: number | null;
}

export const putSystemConfig = (patch: SystemConfigPatch) =>
  apiFetch<{ systemConfig: SystemConfig }>('/api/admin/system-config', {
    method: 'PUT',
    body: JSON.stringify(patch),
  }).then((r) => r.systemConfig);

export interface AiModel {
  id: string;
  label: string;
}

/**
 * Live model list for a key-only provider. `apiKey` previews an unsaved key;
 * omit it to use the saved one. Resolves to a reason string on failure so the
 * UI can explain *why* the dropdown is empty (bad key, rate limit, timeout).
 */
export const listAiModels = (provider: AiProvider, apiKey?: string | null) =>
  apiFetch<{ ok: true; models: AiModel[] }>('/api/admin/system-config/models', {
    method: 'POST',
    body: JSON.stringify({ provider, ...(apiKey ? { apiKey } : {}) }),
  }).then((r) => r.models);

export const listGlobalDeliveries = () =>
  apiFetch<{ deliveries: WebhookDelivery[] }>('/api/admin/system-config/deliveries').then(
    (r) => r.deliveries,
  );

export const sendTestEmail = (to: string) =>
  apiFetch<{ ok: boolean; reason?: string }>('/api/admin/system-config/test-email', {
    method: 'POST',
    body: JSON.stringify({ to }),
  });

// Test the AI connection. Pass the currently-entered (unsaved) values to
// validate before saving; omit a field to fall back to the saved config, and
// omit apiKey (or send '') to reuse the saved key.
export interface AiTestInput {
  provider?: string;
  model?: string | null;
  apiKey?: string | null;
  baseUrl?: string | null;
}
export const testSystemAi = (override?: AiTestInput) =>
  apiFetch<{ ok: boolean; provider?: string; model?: string; text?: string; reason?: string }>(
    '/api/admin/system-config/test-ai',
    { method: 'POST', body: JSON.stringify(override ?? {}) },
  );

export const testTranscription = () =>
  apiFetch<{ ok: boolean; reason?: string }>('/api/admin/system-config/test-transcription', {
    method: 'POST',
  });

// Sentence-case a variable path for a labeled insert, e.g.
//   name → "Name", agent.name → "Agent name", chat_transcript → "Chat transcript".
export function humanizeVar(path: string): string {
  const words = path.replace(/[._]/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Individual context fields (also available as the whole `context` object).
const CONTEXT_VARS = [
  'context.ip', 'context.country', 'context.region', 'context.city',
  'context.user_agent', 'context.language', 'context.timezone',
  'context.origin', 'context.page', 'context',
];

// Ticket events (server: tickets/service.ts resolveTicketContext).
const TICKET_VARS = [
  'name', 'email', 'phone', 'visitor_id', 'session_id',
  'ticket.id', 'ticket.number', 'ticket.subject', 'ticket.description',
  'ticket.status', 'ticket.priority', 'ticket.created_at', 'ticket.updated_at',
  'assignee.name', 'assignee.email', 'site.name', 'site.slug',
];

// The conversation basics every R7 event carries (resolve.ts sessionBasics).
const CONVERSATION_VARS = [
  'name', 'email', 'phone', 'session_id', 'visitor_id', 'medium', 'channel', 'site.name', 'site.slug',
];

// Variable catalogs for the body-template palette (must match resolve.ts).
export const EVENT_VARIABLES: Record<WebhookEvent, string[]> = {
  'visitor.identified': [
    'name', 'email', 'phone', 'visitor_id', 'is_returning',
    'identified_at', 'identified_at_local', 'timezone',
    'site.name', 'site.slug',
    ...CONTEXT_VARS,
    'page_history', 'page_count',
  ],
  'session.ended': [
    'name', 'email', 'phone', 'session_id', 'visitor_id', 'medium',
    // *_local are the readable forms ("2 Sep 2026, 15:53 GMT+5:30") rendered
    // in the display timezone; the bare names stay ISO-8601 UTC for webhook
    // consumers that parse them. Email templates want the _local pair.
    'started_at', 'started_at_local', 'ended_at', 'ended_at_local', 'timezone',
    'ended_by', 'end_reason', 'duration',
    'chat_transcript', 'notes', 'summary', 'video_call_transcript',
    'session_owners', 'agent.name', 'agent.email', 'agent.avatar_url',
    'site.name', 'site.slug',
    ...CONTEXT_VARS,
    'page_history', 'page_count',
  ],
  'ticket.created': TICKET_VARS,
  'ticket.updated': [...TICKET_VARS, 'changes'],
  'session.started': [
    ...CONVERSATION_VARS,
    'started_by', 'started_at', 'owned_by',
    ...CONTEXT_VARS,
  ],
  'message.created': [
    ...CONVERSATION_VARS,
    'message.id', 'message.author', 'message.author_name', 'message.text', 'message.sent_at', 'message.seq',
  ],
  'session.assigned': [
    ...CONVERSATION_VARS,
    'agent.id', 'agent.name', 'agent.email', 'previous_owner', 'via', 'assigned_at',
  ],
  'email.bounced': [
    ...CONVERSATION_VARS,
    'recipient', 'delivery_event', 'reason', 'smtp_status', 'bounce_type', 'mail_kind', 'message_id', 'occurred_at',
  ],
};
